import { reconcileDmTeams } from './dmTeamVisibility';
import { create } from 'zustand';
import type { Channel, DirectChannel, Guild, GuildFolder, Message, PollSummary, VoiceUserState } from '@/types/api';
import type { MatchActivity, MatchActivityPayload } from '@bunker/shared/socketEvents.js';
import type { ChannelReadState } from '@bunker/shared';
import { useAuthStore } from '@/stores/authStore';
import { applyVoiceUserStateReducer, removeVoiceUserReducer } from './voiceReducers';
import {
  noteChannelOpenedReducer,
  markChannelReadReducer,
  bumpChannelUnreadReducer,
  clearChannelUnreadReducer,
  setReadMarkersReducer,
  applyReadMarkerReducer,
  hydrateReadStatesReducer,
  applyReadStateReducer,
} from './unreadReducers';
import {
  applyPollUpdateReducer,
  applyRemoteMessageUpdateReducer,
  applyReactionReducer,
  mergeMessagesReducer,
  prependMessagesReducer,
  removeMessageReducer,
  replaceMessageReducer,
  updateCachedUserReducer,
  upsertMessageReducer,
  type CachedAuthor,
} from './messageReducers';

/**
 * Phase-1 client-side data cache.
 *
 * Mutations land via REST -> server -> WS broadcast -> these reducers. Keeping it
 * Zustand-only (no TanStack mutation cache) so socket pushes are the single source
 * of truth and we avoid stale optimistic state when our own action races a peer.
 */

export interface DataState {
  guilds: Record<string, Guild>;
  guildOrder: string[];
  // Discord-style server folders (per-user rail grouping). Ordered; each
  // carries its member guild ids. Hydrated from /api/me; the rail interleaves
  // these with ungrouped guilds.
  guildFolders: GuildFolder[];
  dmChannels: Record<string, DirectChannel>;
  dmOrder: string[];
  currentGuildId: string | null;
  currentChannelId: string | null;
  // channelId -> messages newest-first
  messagesByChannel: Record<string, Message[]>;
  // userId -> online?
  presence: Record<string, boolean>;
  // channelId -> Set<userId> typing right now
  typingByChannel: Record<string, Record<string, number>>;
  // categoryId -> collapsed?
  collapsedCategoryIds: Record<string, boolean>;
  // channelId -> id of the newest message considered "already read".
  // When a new message arrives in a channel for which this is set, anything
  // newer than the marked id renders below a "НОВОЕ" separator.
  lastReadByChannel: Record<string, string | null>;
  // channelId -> count of unread messages since last visit. Bumped by
  // 'channel:activity' WS events (which fire alongside message:create on
  // the guild room) for any channel that isn't currently in focus. Cleared
  // when the user opens / leaves a channel.
  unreadByChannel: Record<string, number>;
  // channelId -> count of unread messages that explicitly mention me.
  mentionByChannel: Record<string, number>;
  // channelId -> ISO time the server's read state counted through; a live
  // activity not newer than it is already in the counts.
  countedThroughByChannel: Record<string, string>;
  // channelId -> timed live bumps (newest last) — re-added on top of a read
  // state that was computed before them but delivered after.
  liveBumpsByChannel: Record<string, { at: string; mentioned: boolean }[]>;
  // channelId -> { userId -> { messageId, updatedAt } } read pointers of the
  // channel's participants. Drives the ✓/✓✓ receipts on own DM messages.
  // Hydrated on channel open + advanced live by the 'channel:read' WS event.
  readMarkersByChannel: Record<string, Record<string, { messageId: string; updatedAt: string }>>;
  // guildId -> monotonically-increasing member list version. Components that
  // cache roster data can refetch when this bumps.
  // voice channel state (Phase 2A: signaling only, audio comes in 2B)
  voiceByChannel: Record<string, VoiceUserState[]>;
  myVoiceChannelId: string | null;
  matchActivitiesByGuild: Record<string, MatchActivity[]>;

  setGuilds: (guilds: Guild[]) => void;
  setDmChannels: (channels: DirectChannel[]) => void;
  upsertGuild: (g: Guild) => void;
  upsertDmChannel: (c: DirectChannel) => void;
  updateDmTeam: (channelId: string, userId: string, teamTag: string | null) => void;
  removeDmChannel: (id: string) => void;
  removeGuild: (id: string) => void;
  upsertChannel: (c: Channel) => void;
  /** Replace one server's channel list with the one I can see now (a
   *  `guild:channels` push, or an optimistic reorder). Channels that left the
   *  list lose their cached state; the open one falls back to the first chat. */
  setGuildChannels: (guildId: string, channels: Channel[], myPermissions?: string) => void;
  removeChannel: (guildId: string, channelId: string) => void;
  selectGuild: (id: string | null) => void;
  /** Go to the home/welcome screen: clear both the guild AND the active chat
   *  (selectGuild(null) alone no-ops when a DM is open, since guildId is
   *  already null — leaving you stuck in that DM). */
  goHome: () => void;
  selectDirectChannel: (id: string) => void;
  selectChannel: (id: string | null) => void;
  /** Apply a new full guild order (from drag-reorder or the guild:reorder WS event). */
  reorderGuilds: (order: string[]) => void;
  /** Replace the server-folders snapshot (hydration / refetch). */
  setGuildFolders: (folders: GuildFolder[]) => void;
  /** Insert or replace one folder (optimistic create / rename / entry change). */
  upsertGuildFolder: (folder: GuildFolder) => void;
  /** Drop a folder by id (optimistic dissolve). */
  removeGuildFolder: (id: string) => void;

  setMessages: (channelId: string, msgs: Message[]) => void;
  /** Adopt a fetched history snapshot while KEEPING messages that arrived
   *  over the socket during the request (and pending optimistic rows). */
  mergeMessages: (channelId: string, msgs: Message[]) => void;
  prependMessages: (channelId: string, msgs: Message[]) => void;
  upsertMessage: (msg: Message) => void;
  /** A `message:update` broadcast: like upsertMessage, but keeps the
   *  receiver's own poll selection (the broadcast never carries one). */
  applyRemoteMessageUpdate: (msg: Message) => void;
  removeMessage: (channelId: string, id: string) => void;
  /** Drop ALL cached messages for a channel (clear-history / `channel:cleared`). */
  clearChannelMessages: (channelId: string) => void;
  /** Atomic swap: replace a message with a given id (typically a `tmp-…`
   *  optimistic id) with the real message in one reducer pass, so
   *  consumers re-render once instead of twice (remove then upsert). */
  replaceMessage: (channelId: string, oldId: string, next: Message) => void;
  applyReaction: (
    channelId: string,
    messageId: string,
    userId: string,
    emoji: string,
    delta: 1 | -1,
  ) => void;
  /** Splice a live poll update (vote / close from another viewer) into the
   *  cached message. PRESERVES the receiver's own `myOptionIds` — the
   *  broadcast intentionally omits per-user selections, and another user
   *  voting never changes which options *I* picked. */
  applyPollUpdate: (messageId: string, poll: PollSummary) => void;

  setPresence: (userId: string, online: boolean) => void;
  /** Merge many presence updates in one `set` (no-op if no net change). */
  setPresenceBulk: (updates: Record<string, boolean>) => void;
  noteTyping: (channelId: string, userId: string) => void;
  pruneTyping: () => void;
  /** The author's message arrived: stop showing them as typing. */
  clearTyping: (channelId: string, userId: string) => void;
  toggleCategoryCollapsed: (categoryId: string) => void;
  /** Set the unread cutoff if it's not yet set for this channel (idempotent). */
  noteChannelOpened: (channelId: string, newestMessageId: string | null) => void;
  /** Bump cutoff to the newest seen — used when leaving a channel. */
  markChannelRead: (channelId: string, newestMessageId: string | null) => void;
  /** Increment unread badge for a channel (no-op if it's the currently active
   *  one). `guildId` — the channel's server, null/omitted for a DM or group. */
  bumpChannelUnread: (channelId: string, mentioned?: boolean, guildId?: string | null, createdAt?: string) => void;
  /** Reset the unread badge for a channel to 0. */
  clearChannelUnread: (channelId: string) => void;
  /** `ready`: stored read pointers + counts from the server. */
  hydrateReadStates: (states: readonly ChannelReadState[]) => void;
  /** `read:state`: my pointer moved on this or another device. */
  applyReadState: (state: ChannelReadState) => void;
  /** Replace the read-marker map for a channel (hydration on open). */
  setReadMarkers: (
    channelId: string,
    markers: { userId: string; messageId: string; updatedAt: string }[],
  ) => void;
  /** Advance a single participant's read pointer (live 'channel:read' event). */
  applyReadMarker: (
    channelId: string,
    userId: string,
    messageId: string,
    updatedAt: string,
  ) => void;
  /** A user's name or avatar changed: patch the cached messages they wrote. */
  updateCachedUser: (user: CachedAuthor) => void;
  /** Replace the voice rooms snapshot (used on WS ready). */
  setVoiceRooms: (rooms: Record<string, VoiceUserState[]>, myChannelId: string | null) => void;
  /** Apply a single user's voice state change (joined / state-change). */
  applyVoiceUserState: (state: VoiceUserState) => void;
  /** Remove a user from any voice room they were in. */
  removeVoiceUser: (userId: string, channelId: string, joinedAt?: number | null) => void;
  setMatchActivities: (activities: Record<string, MatchActivity[]>) => void;
  applyMatchActivity: (payload: MatchActivityPayload) => void;
}

const TYPING_TTL_MS = 6_000;

/**
 * Drop every channel-scoped map entry whose key is in `channelIds`.
 * Previously this cascade was hand-rolled inside removeChannel and
 * removeDmChannel (5 maps each) — the helper makes the intent clear
 * and prevents future "we added voiceByChannel but forgot one of the
 * call-sites" leaks.
 *
 * Returns an object spread-compatible with the reducer's return value:
 *
 *   return { ...changes, ...pruneChannelScopedMaps(s, [channelId]) };
 */
function pruneChannelScopedMaps(s: DataState, channelIds: string[]) {
  if (channelIds.length === 0) {
    return {
      messagesByChannel: s.messagesByChannel,
      unreadByChannel: s.unreadByChannel,
      mentionByChannel: s.mentionByChannel,
      lastReadByChannel: s.lastReadByChannel,
      countedThroughByChannel: s.countedThroughByChannel,
      liveBumpsByChannel: s.liveBumpsByChannel,
      voiceByChannel: s.voiceByChannel,
    };
  }
  const drop = new Set(channelIds);
  function prune<T>(map: Record<string, T>): Record<string, T> {
    let changed = false;
    const next: Record<string, T> = {};
    for (const [k, v] of Object.entries(map)) {
      if (drop.has(k)) {
        changed = true;
        continue;
      }
      next[k] = v;
    }
    return changed ? next : map;
  }
  return {
    messagesByChannel: prune(s.messagesByChannel),
    unreadByChannel: prune(s.unreadByChannel),
    mentionByChannel: prune(s.mentionByChannel),
    lastReadByChannel: prune(s.lastReadByChannel),
    countedThroughByChannel: prune(s.countedThroughByChannel),
    liveBumpsByChannel: prune(s.liveBumpsByChannel),
    voiceByChannel: prune(s.voiceByChannel),
  };
}

/** Is the page on screen? A hidden tab or a Desktop window in the tray is not
 *  reading the open channel, so its messages still badge (review B1). */
function isPageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

export const useDataStore = create<DataState>((set) => ({
  guilds: {},
  guildOrder: [],
  guildFolders: [],
  dmChannels: {},
  dmOrder: [],
  currentGuildId: null,
  currentChannelId: null,
  messagesByChannel: {},
  presence: {},
  typingByChannel: {},
  collapsedCategoryIds: {},
  lastReadByChannel: {},
  unreadByChannel: {},
  mentionByChannel: {},
  countedThroughByChannel: {},
  liveBumpsByChannel: {},
  readMarkersByChannel: {},
  voiceByChannel: {},
  myVoiceChannelId: null,
  matchActivitiesByGuild: {},

  setGuilds: (guilds) =>
    set(() => {
      const map: Record<string, Guild> = {};
      const order: string[] = [];
      for (const g of guilds) {
        map[g.id] = g;
        order.push(g.id);
      }
      return { guilds: map, guildOrder: order };
    }),

  setGuildFolders: (folders) =>
    set(() => ({ guildFolders: [...folders].sort((a, b) => a.position - b.position) })),

  upsertGuildFolder: (folder) =>
    set((s) => {
      const idx = s.guildFolders.findIndex((f) => f.id === folder.id);
      const next = idx >= 0 ? s.guildFolders.map((f) => (f.id === folder.id ? folder : f)) : [...s.guildFolders, folder];
      return { guildFolders: next.sort((a, b) => a.position - b.position) };
    }),

  removeGuildFolder: (id) =>
    set((s) => {
      if (!s.guildFolders.some((f) => f.id === id)) return s;
      return { guildFolders: s.guildFolders.filter((f) => f.id !== id) };
    }),

  setDmChannels: (channels) =>
    set((s) => {
      const map: Record<string, DirectChannel> = {};
      const order: string[] = [];
      const messagesByChannel = { ...s.messagesByChannel };
      for (const channel of channels) {
        map[channel.id] = channel;
        order.push(channel.id);
        const messages = reconcileDmTeams(messagesByChannel[channel.id], channel);
        if (messages) messagesByChannel[channel.id] = messages;
      }
      return { dmChannels: map, dmOrder: order, messagesByChannel };
    }),

  reorderGuilds: (order) =>
    set((s) => {
      // Keep only ids that still exist in the store; preserve any that the
      // server didn't include (e.g. owned guilds the server skipped) at
      // the end so we don't silently drop them.
      const known = new Set(Object.keys(s.guilds));
      const next = order.filter((id) => known.has(id));
      const missing = s.guildOrder.filter((id) => !next.includes(id));
      return { guildOrder: [...next, ...missing] };
    }),

  upsertGuild: (g) =>
    set((s) => {
      const prev = s.guilds[g.id];
      // `guild:update` after a role/permission change carries only the id:
      // nothing to merge, so keep the reference and spare every subscriber.
      if (prev && (Object.keys(g) as (keyof Guild)[]).every((key) => Object.is(prev[key], g[key]))) return s;
      const exists = !!prev;
      return {
        guilds: { ...s.guilds, [g.id]: { ...s.guilds[g.id], ...g } },
        guildOrder: exists ? s.guildOrder : [...s.guildOrder, g.id],
      };
    }),

  upsertDmChannel: (c) =>
    set((s) => {
      const exists = !!s.dmChannels[c.id];
      const messages = reconcileDmTeams(s.messagesByChannel[c.id], c);
      return {
        ...(messages ? { messagesByChannel: { ...s.messagesByChannel, [c.id]: messages } } : {}),
        dmChannels: { ...s.dmChannels, [c.id]: { ...s.dmChannels[c.id], ...c } },
        dmOrder: exists ? [c.id, ...s.dmOrder.filter((id) => id !== c.id)] : [c.id, ...s.dmOrder],
      };
    }),

  updateDmTeam: (channelId, userId, teamTag) => set((s) => {
    const channel = s.dmChannels[channelId];
    // Refresh only: a hidden/closed conversation must stay hidden.
    if (!channel) return s;
    const updated = { ...channel, directParticipants: channel.directParticipants.map(p =>
      p.userId === userId ? { ...p, user: { ...p.user, teamTag } } : p) };
    const messages = reconcileDmTeams(s.messagesByChannel[channelId], updated);
    return {
      dmChannels: { ...s.dmChannels, [channelId]: updated },
      ...(messages ? { messagesByChannel: { ...s.messagesByChannel, [channelId]: messages } } : {}),
    };
  }),

  removeDmChannel: (id) =>
    set((s) => {
      const { [id]: _gone, ...rest } = s.dmChannels;
      return {
        dmChannels: rest,
        dmOrder: s.dmOrder.filter((d) => d !== id),
        currentChannelId: s.currentChannelId === id ? null : s.currentChannelId,
        // Mirror removeChannel / removeGuild: if we were in voice in this
        // DM, clear the active-voice pointer too. Without this the store
        // could keep `myVoiceChannelId === id` while the backing channel
        // is gone (Codex review #1 on commit 98a9f63).
        myVoiceChannelId:
          s.myVoiceChannelId === id ? null : s.myVoiceChannelId,
        ...pruneChannelScopedMaps(s, [id]),
      };
    }),

  removeGuild: (id) =>
    set((s) => {
      const { [id]: gone, ...rest } = s.guilds;
      // Cascade-clean every channel-scoped map for the channels that
      // lived inside this guild via the shared pruneChannelScopedMaps
      // helper. Plus collapsedCategoryIds (guild-local UI state).
      const ids = gone?.channels.map((c) => c.id) ?? [];
      // Local pruner for category-collapse state (not in the channel-map
      // helper because collapse is keyed by category-id, but channel-ids
      // and category-ids share the same string namespace from the user's
      // perspective).
      const collapsedNext: typeof s.collapsedCategoryIds = {};
      const dropSet = new Set(ids);
      for (const [k, v] of Object.entries(s.collapsedCategoryIds)) {
        if (!dropSet.has(k)) collapsedNext[k] = v;
      }
      return {
        guilds: rest,
        guildOrder: s.guildOrder.filter((g) => g !== id),
        currentGuildId: s.currentGuildId === id ? null : s.currentGuildId,
        currentChannelId: s.currentGuildId === id ? null : s.currentChannelId,
        collapsedCategoryIds: collapsedNext,
        myVoiceChannelId:
          s.myVoiceChannelId && ids.includes(s.myVoiceChannelId)
            ? null
            : s.myVoiceChannelId,
        ...pruneChannelScopedMaps(s, ids),
      };
    }),

  upsertChannel: (c) =>
    set((s) => {
      if (!c.guildId) return s;
      const guild = s.guilds[c.guildId];
      if (!guild) return s;
      // A REST echo (create/edit) carries no `myPermissions`: keep the one the
      // server pushed until the next `guild:channels` replaces it.
      const channels = guild.channels.some((ch) => ch.id === c.id)
        ? guild.channels.map((ch) => (ch.id === c.id ? { myPermissions: ch.myPermissions, ...c } : ch))
        : [...guild.channels, c];
      channels.sort((a, b) => a.position - b.position);
      return {
        guilds: { ...s.guilds, [c.guildId]: { ...guild, channels } },
      };
    }),

  setGuildChannels: (guildId, channels, myPermissions) =>
    set((s) => {
      const guild = s.guilds[guildId];
      if (!guild) return s;
      const sorted = [...channels].sort((a, b) => a.position - b.position);
      const kept = new Set(sorted.map((channel) => channel.id));
      const gone = guild.channels.filter((channel) => !kept.has(channel.id)).map((channel) => channel.id);
      const lostCurrent = s.currentChannelId !== null && gone.includes(s.currentChannelId);
      const firstText = sorted.find((channel) => channel.type === 'TEXT' || channel.type === 'ANNOUNCE');
      return {
        guilds: {
          ...s.guilds,
          [guildId]: { ...guild, channels: sorted, ...(myPermissions !== undefined ? { myPermissions } : {}) },
        },
        ...(lostCurrent
          ? { currentChannelId: s.currentGuildId === guildId ? (firstText?.id ?? null) : null }
          : {}),
        ...pruneChannelScopedMaps(s, gone),
      };
    }),

  removeChannel: (guildId, channelId) =>
    set((s) => {
      const guild = s.guilds[guildId];
      if (!guild) return s;
      return {
        guilds: {
          ...s.guilds,
          [guildId]: {
            ...guild,
            channels: guild.channels.filter((c) => c.id !== channelId),
          },
        },
        currentChannelId:
          s.currentChannelId === channelId ? null : s.currentChannelId,
        myVoiceChannelId:
          s.myVoiceChannelId === channelId ? null : s.myVoiceChannelId,
        ...pruneChannelScopedMaps(s, [channelId]),
      };
    }),

  selectGuild: (id) =>
    set((s) => {
      if (id === s.currentGuildId) return s;
      const guild = id ? s.guilds[id] : undefined;
      const firstText = guild?.channels.find((c) => c.type === 'TEXT' || c.type === 'ANNOUNCE');
      return {
        currentGuildId: id,
        currentChannelId: firstText?.id ?? null,
      };
    }),

  goHome: () => set({ currentGuildId: null, currentChannelId: null }),

  selectDirectChannel: (id) =>
    set(() => ({
      currentGuildId: null,
      currentChannelId: id,
    })),

  selectChannel: (id) => set({ currentChannelId: id }),

  setMessages: (channelId, msgs) =>
    set((s) => ({
      messagesByChannel: { ...s.messagesByChannel, [channelId]: msgs },
    })),
  mergeMessages: (channelId, msgs) => set((s) => mergeMessagesReducer(s, channelId, msgs)),

  prependMessages: (channelId, msgs) =>
    set((s) => prependMessagesReducer(s, channelId, msgs)),

  upsertMessage: (msg) => set((s) => upsertMessageReducer(s, msg)),

  applyRemoteMessageUpdate: (msg) => set((s) => applyRemoteMessageUpdateReducer(s, msg)),

  applyPollUpdate: (messageId, poll) => set((s) => applyPollUpdateReducer(s, messageId, poll)),

  removeMessage: (channelId, id) => set((s) => removeMessageReducer(s, channelId, id)),

  clearChannelMessages: (channelId) =>
    set((s) => ({ messagesByChannel: { ...s.messagesByChannel, [channelId]: [] } })),

  replaceMessage: (channelId, oldId, nextMsg) =>
    set((s) => replaceMessageReducer(s, channelId, oldId, nextMsg)),

  applyReaction: (channelId, messageId, userId, emoji, delta) =>
    set((s) => applyReactionReducer(s, channelId, messageId, userId, emoji, delta)),

  setPresence: (userId, online) =>
    set((s) => {
      // No-op if unchanged so a burst of redundant presence events doesn't
      // spread a new map (and re-render the whole roster) per event.
      if (s.presence[userId] === online) return s;
      return { presence: { ...s.presence, [userId]: online } };
    }),

  setPresenceBulk: (updates) =>
    set((s) => {
      let next: Record<string, boolean> | null = null;
      for (const [userId, online] of Object.entries(updates)) {
        if (s.presence[userId] === online) continue;
        if (next === null) next = { ...s.presence };
        next[userId] = online;
      }
      // No net change — keep the same reference.
      if (next === null) return s;
      return { presence: next };
    }),

  noteTyping: (channelId, userId) =>
    set((s) => {
      const ch = s.typingByChannel[channelId] ?? {};
      return {
        typingByChannel: {
          ...s.typingByChannel,
          [channelId]: { ...ch, [userId]: Date.now() },
        },
      };
    }),

  clearTyping: (channelId, userId) =>
    set((s) => {
      const ch = s.typingByChannel[channelId];
      // Keep the same reference when there is nothing to clear (most messages).
      if (!ch || !(userId in ch)) return s;
      const rest = { ...ch };
      delete rest[userId];
      return { typingByChannel: { ...s.typingByChannel, [channelId]: rest } };
    }),

  pruneTyping: () =>
    set((s) => {
      const cutoff = Date.now() - TYPING_TTL_MS;
      const next: Record<string, Record<string, number>> = {};
      let changed = false;
      for (const [cid, users] of Object.entries(s.typingByChannel)) {
        const fresh: Record<string, number> = {};
        for (const [uid, ts] of Object.entries(users)) {
          if (ts > cutoff) fresh[uid] = ts;
          else changed = true;
        }
        if (Object.keys(fresh).length > 0) next[cid] = fresh;
      }
      // Nothing expired — keep the same reference so Object.is selectors
      // don't re-render every prune tick.
      if (!changed) return s;
      return { typingByChannel: next };
    }),

  toggleCategoryCollapsed: (categoryId) =>
    set((s) => ({
      collapsedCategoryIds: {
        ...s.collapsedCategoryIds,
        [categoryId]: !s.collapsedCategoryIds[categoryId],
      },
    })),

  noteChannelOpened: (channelId, newestMessageId) =>
    set((s) => noteChannelOpenedReducer(s, channelId, newestMessageId)),

  markChannelRead: (channelId, newestMessageId) =>
    set((s) => markChannelReadReducer(s, channelId, newestMessageId)),

  bumpChannelUnread: (channelId, mentioned = false, guildId = null, createdAt) =>
    set((s) => bumpChannelUnreadReducer(s, channelId, mentioned, guildId, isPageVisible(), createdAt)),

  clearChannelUnread: (channelId) => set((s) => clearChannelUnreadReducer(s, channelId)),

  hydrateReadStates: (states) => set((s) => hydrateReadStatesReducer(s, states)),

  applyReadState: (state) => set((s) => applyReadStateReducer(s, state)),

  setReadMarkers: (channelId, markers) => set((s) => setReadMarkersReducer(s, channelId, markers)),

  applyReadMarker: (channelId, userId, messageId, updatedAt) =>
    set((s) => applyReadMarkerReducer(s, channelId, userId, messageId, updatedAt)),

  updateCachedUser: (user) => set((s) => updateCachedUserReducer(s, user)),

  setVoiceRooms: (rooms, myChannelId) =>
    set(() => ({ voiceByChannel: rooms, myVoiceChannelId: myChannelId })),

  applyVoiceUserState: (state) =>
    set((s) => applyVoiceUserStateReducer(s, state, useAuthStore.getState().user?.id ?? null)),

  removeVoiceUser: (userId, channelId, joinedAt) =>
    set((s) =>
      removeVoiceUserReducer(s, userId, channelId, joinedAt, useAuthStore.getState().user?.id ?? null),
    ),

  setMatchActivities: (activities) => set(() => ({ matchActivitiesByGuild: activities })),

  applyMatchActivity: ({ guildId, matchRef, activity }) =>
    set((s) => {
      const current = s.matchActivitiesByGuild[guildId] ?? [];
      const without = current.filter((item) => item.matchRef !== matchRef);
      const next = activity ? [...without, activity] : without;
      const byGuild = { ...s.matchActivitiesByGuild };
      if (next.length > 0) byGuild[guildId] = next;
      else delete byGuild[guildId];
      return { matchActivitiesByGuild: byGuild };
    }),
}));
